<script setup lang="ts">
/**
 * FallbackImage —— 图片加载失败的显式降级包装（F07 Asset-level）。
 *
 * 包裹 `<img>`：正常时渲染业务 `src`，`@error` 时切到构建期内联的
 * 默认占位图（`assets/icons/image-fallback.svg`）+ `aria-label` 显式
 * 标注"图片加载失败" + `console.error` 保留可观测信号。
 *
 * 抽成独立组件是为了在 `v-for` 多图列表中每张图独立持有 `useAssetFallback`
 * 状态（composable 必须在 setup 顶层调用，不能在循环内调）—— 单张图挂掉
 * 只切自身占位，不影响同列表其它图片（F07 acceptance：其他元素不受影响）。
 *
 * **Debug-First**：占位是用户可见显式提示，错误日志走 composable 内
 * `console.error`，不静默吞错。默认图是 SVG 静态资源，构建期已内联，
 * 不会再触发二次 `@error`。
 */
import { computed } from 'vue';

import fallbackUrl from '@/assets/icons/image-fallback.svg';
import { useAssetFallback } from '@/composables/use-asset-fallback';
import { copy } from '@/i18n/zh-CN';

const props = withDefaults(
  defineProps<{
    readonly src: string;
    readonly alt: string;
    /** 日志定位串（如 section id + 图片序号），便于多图列表定位失败项。 */
    readonly context: string;
    readonly loading?: 'lazy' | 'eager';
    readonly fetchPriority?: 'high' | 'low' | 'auto';
  }>(),
  {
    loading: 'lazy',
    fetchPriority: 'auto',
  },
);

const { failed, handleError } = useAssetFallback(props.context);
const srcValue = computed(() => (failed.value ? fallbackUrl : props.src));
const altValue = computed(() =>
  failed.value ? copy.fallback.asset.imageFailedAria : props.alt,
);
</script>

<template>
  <img
    :class="$style.img"
    :src="srcValue"
    :alt="altValue"
    :aria-label="failed ? copy.fallback.asset.imageFailedAria : undefined"
    :loading="props.loading"
    :fetchpriority="props.fetchPriority"
    @error="handleError"
  >
</template>

<style module>
.img {
  width: 100%;
  display: block;
  object-fit: cover;
}
</style>
